import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { designerItemsAPI } from '../../api/designerItemsAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { addToCart, fetchCart } from '../../store/slices/cartSlice';
import { useToast } from '../../components/common/Toast';
import { trackDesignerItemAddedToCart } from '../../utils/analytics';
import { flagAPI, scoresAPI } from '../../api/itemEngagementAPI';
import { toast } from 'react-toastify';
import { trackItemFlagged } from '../../utils/analytics';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import { getDesignerItemPublicPath, isNumericListingRef } from '../../utils/itemLinks';
import { navigateRequireAuth } from '../../utils/authRedirect';
import GuestAuthPrompt from '../../components/auth/GuestAuthPrompt';
import ShopIdentityRow from '../../components/items/ShopIdentityRow';
import ItemImageGallery from '../../components/items/ItemImageGallery';
import OpenInFleaBanner from '../../components/items/OpenInFleaBanner';
import { collectListingMediaItems } from '../../utils/listingImages';
import { resolveShopId, resolveShopLogoUrl } from '../../utils/shopIdentity';
import {
  PlatformBenefitChips,
  FreeListingNotice,
  ListingMetaFields,
  AnonymousSellerNotice,
} from '../../components/items/ListingDetailShared';
import { startNegotiation } from '../../store/slices/negotiationsSlice';
import { logger } from '../../utils/logger';
import { negotiationsAPI } from '../../api/negotiationsAPI';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f6f1e8;
  padding: 2rem 1rem 4rem;
`;

const DetailCard = styled.div`
  max-width: 700px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 1rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.8);
`;

const Content = styled.div`
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
`;

const Price = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #22c55e;
  margin: 1rem 0;
`;

const DesignerAuraBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${p => {
    const s = parseFloat(p.$score);
    if (s >= 80) return 'linear-gradient(135deg, #ffd700, #ffaa00)';
    if (s >= 60) return 'linear-gradient(135deg, #c0c0c0, #a0a0a0)';
    if (s >= 40) return 'linear-gradient(135deg, #cd7f32, #b86a1e)';
    return 'linear-gradient(135deg, #94a3b8, #64748b)';
  }};
  color: ${p => parseFloat(p.$score) >= 40 ? '#333' : '#fff'};
  padding: 0.45rem 0.9rem;
  border-radius: 20px;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
`;

const FlagButton = styled.button`
  background: none; border: none; cursor: pointer;
  color: #9ca3af; font-size: 0.875rem;
  padding: 0.35rem 0.6rem; border-radius: 0.375rem;
  transition: color 0.2s, background 0.2s; margin-top: 0.75rem;
  &:hover { color: #ef4444; background: #fef2f2; }
`;
const FlagOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
`;
const FlagModal = styled.div`
  background: #fff; border-radius: 1rem; padding: 1.75rem;
  width: 100%; max-width: 360px; box-shadow: 0 20px 60px rgba(0,0,0,0.2);
`;
const FlagReasonButton = styled.button`
  display: block; width: 100%; text-align: left;
  padding: 0.75rem 1rem; margin-bottom: 0.5rem;
  border: 2px solid ${p => p.$selected ? '#ef4444' : '#e5e7eb'};
  border-radius: 0.5rem;
  background: ${p => p.$selected ? '#fef2f2' : '#fff'};
  color: #1e293b; font-size: 0.9rem; font-weight: ${p => p.$selected ? '700' : '500'};
  cursor: pointer; transition: all 0.15s;
  &:hover { border-color: #ef4444; background: #fef2f2; }
`;
const FlagSubmitButton = styled.button`
  width: 100%; margin-top: 0.75rem; padding: 0.75rem;
  background: #ef4444; color: #fff; border: none; border-radius: 0.5rem;
  font-weight: 700; font-size: 0.95rem; cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #dc2626; }
`;
const FlagCancelButton = styled.button`
  width: 100%; margin-top: 0.5rem; padding: 0.65rem;
  background: none; color: #64748b; border: 1px solid #e5e7eb;
  border-radius: 0.5rem; font-size: 0.9rem; cursor: pointer;
  &:hover { background: #f8fafc; }
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-top: 1.5rem;
  color: #22c55e;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.25rem;
  align-items: center;
`;

const AddToCartButton = styled.button`
  background: #22c55e;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  font-size: 1rem;
  &:hover:not(:disabled) {
    filter: brightness(0.95);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NegotiateButton = styled(AddToCartButton)`
  background: #ff9800;
`;

const IMAGE_BASE = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://127.0.0.1:8080'
  : '';

function designerAuraDisplay(score) {
  const s = parseFloat(score);
  if (!s || s <= 0) return null;
  const scaled = Math.round(s * 20); // 0-5 → 0-100
  let level = 'New brand';
  if (s >= 4.5) level = 'Excellent';
  else if (s >= 3.5) level = 'Good';
  else if (s >= 2.5) level = 'Average';
  return { score: scaled, level };
}

const DesignerItemDetailPage = () => {
  const { itemRef } = useParams();
  const location = useLocation();
  const stateDesignerId = location?.state?.designerItemId;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { loading: negotiationsLoading } = useSelector((state) => state.negotiations);
  const { showSuccess, showError } = useToast();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);

  const FLAG_REASONS = ['Scam', 'Not as described', 'Damaged', 'Objectionable content', 'Abusive user'];

  const handleFlagSubmit = async () => {
    if (!flagReason) return;
    setFlagSubmitting(true);
    try {
      await flagAPI.flagListing(item.id, 'designer_item', flagReason);
      trackItemFlagged(item.id, 'designer_item', flagReason);
      toast.success('Thank you — your report has been submitted.');
      setFlagModalOpen(false);
      setFlagReason('');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to submit report.';
      toast.error(msg);
    } finally {
      setFlagSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchItem = async () => {
      const ref =
        itemRef ||
        (stateDesignerId != null ? String(stateDesignerId) : null);
      if (!ref) return;
      setLoading(true);
      setError(null);
      try {
        const data = await designerItemsAPI.getDesignerItemByRef(ref);
        setItem(data);
      } catch (err) {
        console.error('DesignerItemDetailPage: Failed to fetch', err);
        setError('Item not found');
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [itemRef, stateDesignerId]);

  useEffect(() => {
    if (item?.item_code && itemRef && isNumericListingRef(itemRef)) {
      navigate(getDesignerItemPublicPath(item), { replace: true });
    }
  }, [item, itemRef, navigate]);

  if (loading) return <LoadingSpinner text="Loading..." />;
  if (error || !item) {
    return (
      <PageWrapper>
        <DetailCard style={{ padding: '2rem', textAlign: 'center' }}>
          <p>{error || 'Item not found'}</p>
          <BackLink to="/designers">← Back to Designers</BackLink>
        </DetailCard>
      </PageWrapper>
    );
  }

  const galleryMedia = collectListingMediaItems(item, IMAGE_BASE);
  const shopId = resolveShopId(item);
  const shopLogoUrl = resolveShopLogoUrl(item, IMAGE_BASE);
  const inStock = Number(item.in_stock) > 0;

  const handleNegotiate = async () => {
    if (!item || !user) {
      navigateRequireAuth(navigate, location, {
        returnTo: item ? getDesignerItemPublicPath(item) : undefined,
      });
      return;
    }
    if (item.owner_id === user.id) {
      toast.error('Cannot negotiate on your own item');
      return;
    }
    try {
      const result = await dispatch(startNegotiation({
        itemId: item.id,
        sellerId: item.owner_id,
        listingType: 'designer_item',
      })).unwrap();
      toast.success('Negotiation started!');
      navigate(`/negotiations/${negotiationsAPI.buildNegotiationRef(result.id)}`);
    } catch (error) {
      logger.error('Negotiation error:', error);
      const errorMessage = error?.error || error?.message || (typeof error === 'string' ? error : 'Failed to start negotiation');
      toast.error(errorMessage);
    }
  };

  const handleAddToCart = async () => {
    if (!item?.id) return;
    if (!user) {
      navigateRequireAuth(navigate, location, {
        returnTo: getDesignerItemPublicPath(item),
      });
      return;
    }
    if (item.owner_id === user.id) {
      showError('You cannot add your own designer listing to the cart.');
      return;
    }
    setAdding(true);
    try {
      await dispatch(
        addToCart({
          listing_id: item.id,
          listing_type: 'designer',
          quantity: 1,
        })
      ).unwrap();
      await dispatch(fetchCart());
      const price = parseFloat(item.price || 0);
      trackDesignerItemAddedToCart(item.id, { item_price: price, quantity: 1 });
      scoresAPI.recordCartAdd(item.id, 'designer_item').catch(() => {});
      showSuccess('Added to cart', 2500);
    } catch (e) {
      const msg = e?.error || e?.message || 'Could not add to cart.';
      showError(typeof msg === 'string' ? msg : 'Could not add to cart.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
    <PageWrapper>
      <DetailCard>
        <ItemImageGallery media={galleryMedia} alt={item.title} beige />
        <Content>
          <ShopIdentityRow
            name={item.brand_name}
            logoUrl={shopLogoUrl}
            to={shopId ? `/designer-shop/${designerItemsAPI.buildShopRef(shopId)}` : null}
          />
          <Title>{item.title}</Title>
          <OpenInFleaBanner path={getDesignerItemPublicPath(item)} />
          <AnonymousSellerNotice isAnonymous={item.is_anonymous} />
          {(() => {
            const aura = designerAuraDisplay(item.designer_aura);
            return aura ? (
              <DesignerAuraBadge $score={aura.score}>
                <span>⭐</span>
                <span>Designer Aura: {aura.score}</span>
                <span style={{ fontWeight: 400, opacity: 0.85 }}>{aura.level}</span>
              </DesignerAuraBadge>
            ) : null;
          })()}
          <Price>{formatItemPriceEgp(item.price)}</Price>
          <FreeListingNotice item={item} />
          <PlatformBenefitChips item={item} />
          <ListingMetaFields
            description={item.description}
            category={item.category}
            subcategory={item.subcategory}
            bookSubcategory={item.book_subcategory}
            showCondition={false}
            color={item.color}
            size={item.size}
            availableSizes={item.available_sizes}
            itemCode={item.item_code}
            itemFlaws={item.item_flaws}
          />
          <ActionRow>
            {user && item.owner_id !== user.id && (
              <>
                <AddToCartButton type="button" onClick={handleAddToCart} disabled={adding || !inStock}>
                  {adding ? 'Adding…' : (inStock ? 'Add To Cart' : 'Out of Stock')}
                </AddToCartButton>
                <NegotiateButton
                  type="button"
                  onClick={handleNegotiate}
                  disabled={negotiationsLoading}
                >
                  {negotiationsLoading ? 'Starting...' : 'Negotiate'}
                </NegotiateButton>
              </>
            )}
            {user && item.owner_id === user.id && (
              <span style={{ color: '#64748b', fontSize: '0.9rem' }}>
                This is your item. You cannot add it to cart or negotiate.
              </span>
            )}
            {!user && (
              <GuestAuthPrompt
                message="Create an account to continue"
                returnTo={getDesignerItemPublicPath(item)}
              />
            )}
          </ActionRow>
          {user && item.owner_id !== user.id && (
            <FlagButton type="button" onClick={() => { setFlagModalOpen(true); setFlagReason(''); }}>
              🚩 Report listing
            </FlagButton>
          )}
          <BackLink to="/designers">← Back to Designers</BackLink>
        </Content>
      </DetailCard>
    </PageWrapper>

    {flagModalOpen && (
      <FlagOverlay onClick={() => setFlagModalOpen(false)}>
        <FlagModal onClick={e => e.stopPropagation()}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', margin: '0 0 0.35rem' }}>🚩 Report this listing</h3>
          <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 1.25rem' }}>Select the reason for your report:</p>
          {FLAG_REASONS.map(r => (
            <FlagReasonButton key={r} type="button" $selected={flagReason === r} onClick={() => setFlagReason(r)}>
              {r}
            </FlagReasonButton>
          ))}
          <FlagSubmitButton type="button" disabled={!flagReason || flagSubmitting} onClick={handleFlagSubmit}>
            {flagSubmitting ? 'Submitting…' : 'Submit Report'}
          </FlagSubmitButton>
          <FlagCancelButton type="button" onClick={() => setFlagModalOpen(false)}>Cancel</FlagCancelButton>
        </FlagModal>
      </FlagOverlay>
    )}
    </>
  );
};

export default DesignerItemDetailPage;
